import { useEffect, useMemo, useRef, useState } from "react";
import { CheckCircle2, CircleAlert, X } from "lucide-react";
import { Shell, type PageId } from "./components/Shell";
import { createEmptyDataset } from "./data/empty";
import { analyseDataset, makeLocationPlan } from "./lib/analytics";
import { parseDatasetFile } from "./lib/csv";
import {
  loadDataset,
  loadMediaBlob,
  loadProfile,
  resetLegacyBusinessData,
  saveDataset,
  saveMediaBlob,
  saveProfile,
} from "./lib/storage";
import type { UserProfile, WarehouseDataset } from "./types";
import { OverviewPage } from "./pages/OverviewPage";
import { ProductsPage } from "./pages/ProductsPage";
import { WarehousePage } from "./pages/WarehousePage";
import { BatchesPage } from "./pages/BatchesPage";
import { ReplenishmentPage } from "./pages/ReplenishmentPage";
import { AssistantPage } from "./pages/AssistantPage";
import { ProfilePage } from "./pages/ProfilePage";
import { SupportWidget } from "./components/SupportWidget";

const defaultProfile: UserProfile = {
  id: "",
  name: "",
  gender: "",
  birthday: "",
  company: "",
  role: "",
  email: "",
  avatarDataUrl: "",
  warehouse: "昆山大仓",
};

const pageIds: PageId[] = ["overview", "products", "warehouse", "batches", "replenishment", "assistant", "profile"];

function initialPage(): PageId {
  const fromHash = window.location.hash.replace("#/", "") as PageId;
  return pageIds.includes(fromHash) ? fromHash : "overview";
}

type Toast = { tone: "success" | "error"; title: string; detail: string } | null;

export default function App() {
  const [page, setPage] = useState<PageId>(initialPage);
  const [dataset, setDataset] = useState<WarehouseDataset>(() => createEmptyDataset());
  const [profile, setProfile] = useState<UserProfile>(() => loadProfile() ?? defaultProfile);
  const [toast, setToast] = useState<Toast>(null);
  const [supportOpen, setSupportOpen] = useState(false);
  const [mediaUrl, setMediaUrl] = useState("/assets/cangxu-warehouse-poster.jpg");
  const [mediaType, setMediaType] = useState<"image" | "video">("image");
  const [isImporting, setIsImporting] = useState(false);
  const importInputRef = useRef<HTMLInputElement>(null);
  const mediaObjectUrl = useRef<string | null>(null);
  const importProtectedRef = useRef(false);
  const savedDatasetRef = useRef<WarehouseDataset | null>(null);

  const locations = useMemo(() => makeLocationPlan(dataset), [dataset]);
  const plan = useMemo(() => analyseDataset(dataset, locations), [dataset, locations]);

  useEffect(() => {
    let active = true;
    resetLegacyBusinessData()
      .then((wasReset) => (wasReset ? null : loadDataset()))
      .then((saved) => {
        savedDatasetRef.current = saved;
        if (active && saved && !importProtectedRef.current) setDataset(saved);
      })
      .catch(() => undefined);
    loadMediaBlob("space-media").then((blob) => {
      if (!active || !blob) return;
      const url = URL.createObjectURL(blob);
      mediaObjectUrl.current = url;
      setMediaUrl(url);
      setMediaType(blob.type.startsWith("video/") ? "video" : "image");
    }).catch(() => undefined);
    return () => {
      active = false;
      if (mediaObjectUrl.current) URL.revokeObjectURL(mediaObjectUrl.current);
    };
  }, []);

  useEffect(() => {
    const onHashChange = () => {
      setPage(initialPage());
      setSupportOpen(false);
    };
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 5000);
    return () => window.clearTimeout(timer);
  }, [toast]);

  function navigate(next: PageId) {
    setSupportOpen(false);
    setPage(next);
    window.location.hash = `/${next}`;
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function importDataset(file: File) {
    importProtectedRef.current = true;
    setIsImporting(true);
    try {
      const next = await parseDatasetFile(file);
      setDataset(next);
      await saveDataset(next);
      setToast({
        tone: "success",
        title: "数据已经读懂",
        detail: `识别 ${next.sales.length.toLocaleString("zh-CN")} 条记录、${new Set(next.sales.map((row) => row.sku)).size} 个品项，决策方案已重新计算。`,
      });
    } catch (error) {
      importProtectedRef.current = false;
      if (savedDatasetRef.current) setDataset(savedDatasetRef.current);
      setToast({
        tone: "error",
        title: "数据没有导入",
        detail: error instanceof Error ? error.message : "请检查文件格式后重试。",
      });
    } finally {
      setIsImporting(false);
      if (importInputRef.current) importInputRef.current.value = "";
    }
  }

  async function replaceMedia(file: File) {
    if (!file.type.startsWith("image/") && !file.type.startsWith("video/")) {
      setToast({ tone: "error", title: "文件类型不支持", detail: "请选择图片或视频文件。" });
      return;
    }
    await saveMediaBlob("space-media", file);
    if (mediaObjectUrl.current) URL.revokeObjectURL(mediaObjectUrl.current);
    const nextUrl = URL.createObjectURL(file);
    mediaObjectUrl.current = nextUrl;
    setMediaUrl(nextUrl);
    setMediaType(file.type.startsWith("video/") ? "video" : "image");
    setToast({ tone: "success", title: "空间影像已替换", detail: "新内容已保存在当前浏览器中。" });
  }

  function restoreMedia() {
    if (mediaObjectUrl.current) URL.revokeObjectURL(mediaObjectUrl.current);
    mediaObjectUrl.current = null;
    setMediaUrl("/assets/cangxu-warehouse-poster.jpg");
    setMediaType("image");
    setToast({ tone: "success", title: "已恢复默认影像", detail: "你仍可随时导入自己的仓库影像。" });
  }

  function updateProfile(profileNext: UserProfile) {
    setProfile(profileNext);
    saveProfile(profileNext);
  }

  return (
    <>
      <Shell
        page={page}
        dataset={dataset}
        quality={plan.quality}
        profile={profile}
        onNavigate={navigate}
        onImport={() => importInputRef.current?.click()}
        onOpenSupport={() => setSupportOpen(true)}
      >
        {page === "overview" && (
          <OverviewPage
            dataset={dataset}
            plan={plan}
            locations={locations}
            mediaUrl={mediaUrl}
            mediaType={mediaType}
            onNavigate={navigate}
            onImport={async (file) => {
              await importDataset(file);
            }}
            onReplaceMedia={replaceMedia}
            onRestoreMedia={restoreMedia}
          />
        )}
        {page === "products" && <ProductsPage plan={plan} onNavigate={navigate} />}
        {page === "warehouse" && (
          <WarehousePage dataset={dataset} plan={plan} onNavigate={navigate} />
        )}
        {page === "batches" && <BatchesPage dataset={dataset} plan={plan} />}
        {page === "replenishment" && <ReplenishmentPage plan={plan} onNavigate={navigate} />}
        {page === "assistant" && (
          <AssistantPage
            dataset={dataset}
            plan={plan}
            locations={locations}
            onImport={() => importInputRef.current?.click()}
            onOpenSupport={() => setSupportOpen(true)}
          />
        )}
        {page === "profile" && (
          <ProfilePage
            profile={profile}
            onSave={updateProfile}
            onOpenSupport={() => setSupportOpen(true)}
          />
        )}
      </Shell>

      <SupportWidget
        open={supportOpen}
        launcherVisible={page !== "profile"}
        onOpen={() => setSupportOpen(true)}
        onClose={() => setSupportOpen(false)}
        dataset={dataset}
        plan={plan}
        locations={locations}
      />

      <input
        ref={importInputRef}
        type="file"
        accept=".csv,.json,text/csv,application/json"
        className="sr-only"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) void importDataset(file);
        }}
      />

      {isImporting && <div className="import-progress" role="status"><span />正在读取并分析数据</div>}

      {toast && (
        <div className={`toast toast-${toast.tone}`} role="status">
          <div className="toast-icon">
            {toast.tone === "success" ? <CheckCircle2 size={19} /> : <CircleAlert size={19} />}
          </div>
          <div>
            <strong>{toast.title}</strong>
            <p>{toast.detail}</p>
          </div>
          <button className="icon-button" aria-label="关闭通知" onClick={() => setToast(null)}>
            <X size={16} />
          </button>
        </div>
      )}
    </>
  );
}
